<template>
  <div class="autocomplete-dropdown__container pt-1 right-0 left-0 top-12 absolute lg:pt-2 lg:top-8">
    <div v-if="!b2b" class="autocomplete-dropdown__arrow w-0 h-0 hidden lg:block" />
    <ul class="autocomplete-dropdown__list shadow-md" tabindex="0" role="listbox" aria-labelledby="search-label">
      <li
        v-for="item in searchSuggestionData"
        :id="item.id"
        :key="item.value"
        qaid="autocomplete-dropdown-list-item-suggestions"
        class="suggestions autocomplete-dropdown__list-item border-t border-gray-30 bg-white leading-normal"
        :aria-selected="isSelected(item)"
        role="option"
      >
        <a
          qaid="autocomplete-dropdown-list-item-suggestions-link"
          class="p-1 uppercase ml-1 lg:ml-0 flex items-center hover:bg-gray-05 focus:bg-gray-05 hover:text-gray-100 focus:text-gray-100"
          :class="{
            'bg-gray-05 text-gray-100 outline outline-2 outline-blue-100': isSelected(item),
          }"
          :href="item.url"
          @click="$emit('onSelected', item.value)"
        >
          {{ item.value }}
        </a>
      </li>
      <li
        v-if="b2b && searchCategoriesData.length > 0"
        :id="`b2b-category-suggestion`"
        class="autocomplete-dropdown__list-header bg-gray-05 p-1 leading-normal text-xs text-gray-80 text-left font-bold"
        qaid="autocomplete-dropdown-categories-header"
        role="presentation"
      >
        {{ $t('text.categories') }}
      </li>
      <li
        v-for="item in searchCategoriesData"
        :id="item.id"
        :key="item.value"
        qaid="autocomplete-dropdown-list-item-categories"
        class="categories autocomplete-dropdown__list-item border-t border-gray-30 bg-white leading-normal"
        :aria-selected="isSelected(item)"
        role="option"
      >
        <a
          qaid="autocomplete-dropdown-list-item-categories-link"
          class="p-2 lg:p-1 text-gray-100 font-normal flex items-center hover:bg-gray-05 focus:bg-gray-05 hover:text-gray-100 focus:text-gray-100"
          :class="{
            'bg-gray-05 text-gray-100 outline outline-2 outline-blue-100': isSelected(item),
          }"
          :href="item.url"
          @click="$emit('onSelected', item.value)"
        >
          {{ item.value }}
        </a>
      </li>
      <li
        v-if="b2b && searchProductsData.length > 0"
        :id="`b2b-product-suggestion`"
        class="autocomplete-dropdown__list-header bg-gray-05 p-1 leading-normal text-xs text-gray-80 text-left font-bold"
        qaid="autocomplete-dropdown-products-header"
        role="presentation"
      >
        {{ $t('text.products') }}
      </li>
      <li
        v-for="item in searchProductsData"
        :id="item.id"
        :key="item.value"
        class="products autocomplete-dropdown__list-item border-t border-gray-30 bg-white leading-normal"
        qaid="autocomplete-dropdown-list-item-products"
        :aria-selected="isSelected(item)"
        role="option"
      >
        <a
          qaid="autocomplete-dropdown-list-item-products-link"
          :href="item.url"
          class="text-gray-100 font-normal flex items-center hover:bg-gray-05 focus:bg-gray-05 hover:text-gray-100 focus:text-gray-100"
          :class="{
            'bg-gray-05 text-gray-100 outline outline-2 outline-blue-100': isSelected(item),
          }"
          @click="$emit('onSelected', item.value)"
        >
          <div v-if="!b2b" class="autocomplete-dropdown__list-item-thumb w-24 p-1 hidden md:block">
            <img qaid="autocomplete-dropdown-list-item-products-img" alt="" :src="item.image" />
          </div>
          <div class="autocomplete-dropdown__list-item-desc flex m-1 overflow-x-hidden p-1 lg:p-0">
            <span
              qaid="autocomplete-dropdown-list-item-products-title"
              class="title whitespace-nowrap overflow-x-hidden"
              >{{ item.value }}</span
            >
            <span
              qaid="autocomplete-dropdown-list-item-products-price"
              class="price font-bold text-right ml-2 whitespace-nowrap"
              >{{ item.price }}</span
            >
          </div>
        </a>
      </li>
    </ul>
  </div>
</template>
<script>
import {mapState} from 'vuex';
export default {
  props: {
    result: {
      type: Object,
      required: true,
      default: () => {
        return {
          categories: [],
          products: [],
          suggestions: [],
        };
      },
    },
    displayImages: {
      type: String,
      default: 'true',
    },
    selectedId: {
      type: String,
      default: '',
    },
  },
  computed: {
    allData() {
      return [...this.searchSuggestionData, ...this.searchCategoriesData, ...this.searchProductsData];
    },
    nextOption() {
      const nextIndex = this.currentIndex + 1;
      return nextIndex < this.allData.length ? this.allData[nextIndex] : null;
    },
    previousOption() {
      const previousIndex = this.currentIndex - 1;
      return previousIndex >= 0 ? this.allData[previousIndex] : null;
    },
    currentIndex() {
      return this.allData.findIndex((item) => item.id === this.selectedId);
    },
    searchSuggestionData() {
      return this.result.suggestions.map((item) => {
        return {
          value: item.term,
          url: `${this.encodedContextPath}/search?text=${item.term}`,
          id: this.formatId(item.term, 'suggestion'),
        };
      });
    },
    searchProductsData() {
      return this.result.products.map((item) => {
        const val = item?.marketingModelYear && this.b2b ? `${item.name} (${item.marketingModelYear})` : item.name;
        return {
          value: val,
          price: item.price != null ? item.price.formattedValue : '',
          image: item.images.length > 0 && this.displayImages === 'true' ? item.images[0].url : null,
          url: this.encodedContextPath + item.url,
          id: this.formatId(val, 'product_suggestion'),
        };
      });
    },
    searchCategoriesData() {
      return this.result.categories.map((item) => {
        return {
          value: item.name,
          url: this.encodedContextPath + item.url,
          id: this.formatId(item.name, 'category_suggestion'),
        };
      });
    },
    ...mapState('backend', ['encodedContextPath', 'b2b']),
  },
  methods: {
    isSelected(item) {
      return item.id === this.selectedId;
    },
    getNextId() {
      return this.nextOption?.id;
    },

    getPreviousId() {
      return this.previousOption?.id;
    },

    triggerSelected() {
      document.querySelector(`#${this.selectedId} a`)?.click();
    },

    formatId(text, suffix) {
      let id = text.replace(/[\s():-]+/g, '_');

      if (!this.isValidCSSIdentifier(`${id}_${suffix}`)) {
        id = `id_${id}`;
      }
      const suffixedId = `${id}_${suffix}`;

      return suffixedId.replace(/_+/g, '_');
    },
    isValidCSSIdentifier(val) {
      /** Checking if value starts with a digit*/
      if (/^\d/.test(val.charAt(0))) {
        return false;
      }

      /** Checking every char should either be stadnard char or valid ISO10646 char */
      return [...val].every((char) => /^[a-zA-Z0-9_]$/.test(char) || char.codePointAt(0) >= 0x00a0);
    },
  },
};
</script>
<style lang="scss" scoped>
.autocomplete-dropdown {
  &__arrow {
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid var(--white);
    position: absolute;
    right: 13px;
    top: 11px;
  }

  &__container {
    z-index: 16000009;

    @media (screen(lg)) {
      width: 36rem;
      left: unset;
    }
  }

  &__list-item {
    &:first-child {
      border-top: 0;
    }
  }

  &__list-item-desc {
    text-overflow: ellipsis;
    flex-basis: 100%;

    .title {
      text-overflow: ellipsis;
      flex-grow: 1;
    }
  }
}
</style>
